import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { LocalUserRoles } from '@buspro/shared/common';
import { NzDrawerComponent, NzDrawerContentDirective } from 'ng-zorro-antd/drawer';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { AvatarModule } from 'ngx-avatars';

interface LocalUserPresenceMember {
  id: string;
  info: {
    name: string;
    role: string;
  };
}

@Component({
  selector: 'buspro-admin-online-users',
  standalone: true,
  imports: [NgIf, NgFor, NzDrawerComponent, NzDrawerContentDirective, AvatarModule, NzTagComponent],
  template: `
    <button
      (click)="showDrawer.set(true)"
      class="text-gray-600 text-3xl cursor-pointer hover:bg-gray-100 p-3 rounded-full relative"
    >
      <span class="fe fe-users"></span>
      @if(onlineUsers > 0) {
        <span class="absolute top-1 right-1 flex h-5 w-5 items-center justify-center">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-5 w-5 bg-green-500 text-white text-xs items-center justify-center font-semibold">
            {{ onlineUsers }}
          </span>
        </span>
      }
    </button>

    <nz-drawer
      [nzVisible]="showDrawer()"
      [nzTitle]="drawerTitle"
      [nzPlacement]="'right'"
      [nzWidth]="400"
      (nzOnClose)="onDrawerClose()"
      (nzVisibleChange)="onDrawerVisibleChange($event)"
    >
      <ng-template #drawerTitle>
        <div class="flex items-center gap-3">
          <span class="fe fe-users text-xl text-primary"></span>
          <div>
            <h3 class="text-lg font-semibold m-0">Online Local Users</h3>
            <p class="text-sm text-gray-500 m-0">{{ onlineUsers }} currently online</p>
          </div>
        </div>
      </ng-template>

      <ng-container *nzDrawerContent>
        @if(localUsers().length > 0) {
          <div class="space-y-4">
            <div class="space-y-2">
              @for (user of localUsers(); track user.id) {
                <div class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-100">
                  <div class="relative">
                    <ngx-avatars [name]="user.info.name" [size]="40" class="rounded-full"></ngx-avatars>
                    <div class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ user.info.name }}</p>
                    <div class="flex items-center gap-2 mt-1">
                      <nz-tag [nzColor]="getRoleColor(user.info.role)" class="text-xs">
                        <span class="fe {{ getRoleIcon(user.info.role) }} mr-1"></span>
                        {{ user.info.role }}
                      </nz-tag>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="flex flex-col items-center justify-center py-12 text-center">
            <span class="fe fe-users text-6xl text-gray-300 mb-4"></span>
            <h3 class="text-lg font-semibold text-gray-700 mb-2">No Users Online</h3>
            <p class="text-sm text-gray-500">There are currently no local users online.</p>
          </div>
        }
      </ng-container>
    </nz-drawer>
  `,
  styles: `
    :host {
      display: inline-block;
    }
  `,
})
export class OnlineUsersComponent implements OnChanges {
  @Input() onlineUsers = 0;
  @Input() localUsersData: LocalUserPresenceMember[] = [];

  showDrawer = signal(false);
  localUsers = signal<LocalUserPresenceMember[]>([]);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['localUsersData']) {
      this.localUsers.set(this.localUsersData || []);
    }
  }

  onDrawerVisibleChange(visible: boolean) {
    this.showDrawer.set(visible);
  }

  onDrawerClose() {
    this.showDrawer.set(false);
  }

  getRoleColor(role: string): string {
    // Color code by role type
    switch (role) {
      case LocalUserRoles.LOCAL_ADMIN:
        return 'red';
      case LocalUserRoles.LOCAL_MANAGER:
        return 'blue';
      case LocalUserRoles.LOCAL_DEVELOPER:
        return 'purple';
      case LocalUserRoles.LOCAL_AUDITOR:
        return 'cyan';
      case LocalUserRoles.LOCAL_TECHNICIAN:
        return 'orange';
      case LocalUserRoles.BUSPRO_BUS_STOP_MANAGER:
        return 'geekblue';
      case LocalUserRoles.BUSPRO_BUS_STOP_CLERK:
        return 'green';
      default:
        return 'default';
    }
  }

  getRoleIcon(role: string): string {
    // Icon per role type
    switch (role) {
      case LocalUserRoles.LOCAL_ADMIN:
        return 'fe-shield';
      case LocalUserRoles.LOCAL_MANAGER:
        return 'fe-briefcase';
      case LocalUserRoles.LOCAL_DEVELOPER:
        return 'fe-code';
      case LocalUserRoles.LOCAL_AUDITOR:
        return 'fe-search';
      case LocalUserRoles.LOCAL_TECHNICIAN:
        return 'fe-tool';
      case LocalUserRoles.BUSPRO_BUS_STOP_MANAGER:
      case LocalUserRoles.BUSPRO_BUS_STOP_CLERK:
        return 'fe-map-pin';
      default:
        return 'fe-user';
    }
  }
}
